import React from 'react';
import { Image, Pressable, View } from 'react-native';

import { HStack, Text, VStack, theme } from '@squire/ui';

import i18n from '@/constants/i18n';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { openDirections } from '@/utils/openDirections';

import { styles } from './styles';
import type { ShopDirectionsProps } from './types';

const ShopDirections: React.FC<ShopDirectionsProps> = ({
  shopName,
  address,
  staticMapUrl,
  latitude,
  longitude,
  googlePlaceId,
}) => {
  const { t } = useScreenTranslations('ViewReservationShopInfo');

  const handleGetDirections = async () => {
    if (latitude === null || longitude === null) {
      return;
    }

    try {
      await openDirections({
        latitude,
        longitude,
        locale: i18n.language,
        name: shopName,
        googlePlaceId,
      });
    } catch {
      // Error is already logged in openDirections utility
    }
  };

  return (
    <HStack space={theme.spacing[5]} style={styles.rowStart}>
      {/* Map Preview */}
      {staticMapUrl && (
        <View style={styles.mapPreviewContainer}>
          <Image
            source={{ uri: staticMapUrl }}
            style={styles.mapPreview}
            resizeMode="cover"
            accessibilityLabel={t('accessibility.mapPreview')}
          />
          <View style={styles.mapPinOverlay}>
            <View style={styles.mapPin} />
          </View>
        </View>
      )}

      {/* Shop Info */}
      <VStack flex space={theme.spacing[4]}>
        <VStack flex space={theme.spacing[1]}>
          <Text variant="lStrong" color="textPrimary">
            {shopName}
          </Text>
          <Text variant="m" color="textSecondary">
            {address}
          </Text>
        </VStack>
        <Pressable
          onPress={handleGetDirections}
          accessibilityRole="link"
          accessibilityLabel={t('accessibility.getDirections')}
        >
          <Text variant="m" color="textLink">
            {t('getDirections')}
          </Text>
        </Pressable>
      </VStack>
    </HStack>
  );
};

ShopDirections.displayName = 'ShopDirections';
export { ShopDirections };
